import { Select, TextField, ToggleField } from '~/components/form';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSection,
  FormActions,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import {
  CHANNEL_LABELS,
  credentialKeys,
  credentialLabel,
  isSupportedProvider,
  PROVIDERS_BY_CHANNEL,
  providerKeySchema,
  providerLabel,
  providersForChannel,
  SAVED_CREDENTIAL_MASK,
  type ProviderKeyFormValues,
} from '~/lib/notification/providerKeyForm';
import type { NotificationChannel, ProviderKey } from '~/modules/query/notification/types';
import { routes } from '~/app/routes';

const FORM_ID = 'provider-key-form';

const CREATE_CHANNELS = Object.keys(PROVIDERS_BY_CHANNEL) as (keyof typeof PROVIDERS_BY_CHANNEL)[];

interface ProviderKeyFormProps {
  draft: ProviderKeyFormValues;
  patch: (changes: Partial<ProviderKeyFormValues>) => void;
  /** The saved key; absent while creating. It carries no credentials, only whether some are saved. */
  stored?: ProviderKey;
  dirty: boolean;
  busy: boolean;
  /** A failed save, as a sentence for the `Alert`. */
  error: string | null;
  onSubmit: () => void;
}

const CHANNEL_KEY_COPY: Record<
  NotificationChannel,
  { label: string; helper: string; placeholder: string }
> = {
  email: {
    label: 'Sending domain',
    helper: 'Messages sent from this domain use this key.',
    placeholder: 'mail.example.com',
  },
  sms: {
    label: 'Phone number',
    helper: 'Messages sent from this number use this key.',
    placeholder: '+15555550123',
  },
  browser: {
    label: 'Channel key',
    helper: 'Routes messages to this key.',
    placeholder: '',
  },
};

/** The provider key form body and footer, shared by `/notifications/provider-keys/new` and `/:id`. */
export default function ProviderKeyForm({
  draft,
  patch,
  stored,
  dirty,
  busy,
  error,
  onSubmit,
}: ProviderKeyFormProps) {
  const isNew = stored === undefined;
  const check = useFormValidation(
    providerKeySchema(stored),
    draft,
    (field) => `provider-key-${field}`,
  );
  const keys = credentialKeys(draft.provider);
  // Blank fields keep the saved set only while the provider is unchanged and one is saved.
  const canKeep = !!stored?.hasCredentials && stored.provider === draft.provider;
  const providerLocked = !isNew && !isSupportedProvider(draft.channel, draft.provider);
  const copy = CHANNEL_KEY_COPY[draft.channel];

  const channelOptions = CREATE_CHANNELS.map((channel) => ({
    value: channel,
    label: CHANNEL_LABELS[channel],
  }));
  const providerOptions = providersForChannel(draft.channel).map((provider) => ({
    value: provider,
    label: providerLabel(provider),
  }));

  return (
    <form
      id={FORM_ID}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (check.submit()) onSubmit();
      }}
    >
      <Card>
        <CardHeader title="Details" />
        <CardBody className="space-y-6">
          {error && <Alert variant="error" message={error} />}

          <CardSection title="Provider" description="The service that delivers the messages.">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {isNew ? (
                <Select
                  id="provider-key-channel"
                  label="Channel"
                  required
                  options={channelOptions}
                  value={draft.channel}
                  onChange={(channel) => {
                    const next = channel as keyof typeof PROVIDERS_BY_CHANNEL;
                    patch({
                      channel: next,
                      provider: PROVIDERS_BY_CHANNEL[next][0],
                      credentials: {},
                      channelKey: '',
                      defaultFrom: '',
                    });
                  }}
                />
              ) : (
                <TextField
                  id="provider-key-channel"
                  testId="provider-key-channel"
                  label="Channel"
                  readOnly
                  value={CHANNEL_LABELS[draft.channel] ?? draft.channel}
                  onChange={() => {}}
                  helperText="A key's channel cannot change. Create a new key instead."
                />
              )}
              {providerLocked ? (
                <TextField
                  id="provider-key-provider"
                  testId="provider-key-provider"
                  label="Provider"
                  readOnly
                  value={providerLabel(draft.provider)}
                  onChange={() => {}}
                />
              ) : (
                <Select
                  id="provider-key-provider"
                  label="Provider"
                  required
                  options={providerOptions}
                  value={draft.provider}
                  onChange={(provider) => patch({ provider, credentials: {} })}
                />
              )}
              <TextField
                id="provider-key-customerId"
                testId="provider-key-customerId"
                label="Customer ID"
                code
                value={draft.customerId}
                onChange={(customerId) => patch({ customerId })}
                helperText="Leave blank for a key every customer can use."
              />
            </div>
          </CardSection>

          <CardSection
            title="Credentials"
            description={
              isNew
                ? 'Enter every credential. They are stored encrypted.'
                : canKeep
                  ? 'Saved credentials are hidden. Leave every field blank to keep them, or fill in every field to replace them.'
                  : 'No saved credentials for this provider. Enter every credential.'
            }
          >
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {keys.map((key) => (
                <TextField
                  key={`${draft.provider}-${key}`}
                  id={`provider-key-credential-${key}`}
                  testId={`provider-key-credential-${key}`}
                  label={credentialLabel(key)}
                  type="password"
                  code
                  required={!canKeep}
                  autoComplete="new-password"
                  value={draft.credentials[key] ?? ''}
                  onChange={(value) =>
                    patch({ credentials: { ...draft.credentials, [key]: value } })
                  }
                  onBlur={() => check.touch(`credential-${key}`)}
                  error={check.errors[`credential-${key}`]}
                  placeholder={canKeep ? SAVED_CREDENTIAL_MASK : undefined}
                />
              ))}
              {keys.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This provider's credentials are kept as they are.
                </p>
              )}
            </div>
          </CardSection>

          <CardSection title="Routing" description="Which messages use this key.">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <ToggleField
                id="provider-key-isDefault"
                testId="provider-key-isDefault"
                label="Default"
                text="Default provider for this channel"
                helperText="A default key is used when no keyed provider matches."
                checked={draft.isDefault}
                onChange={(isDefault) =>
                  patch({ isDefault, channelKey: isDefault ? '' : draft.channelKey })
                }
              />
              {!draft.isDefault && (
                <TextField
                  id="provider-key-channelKey"
                  testId="provider-key-channelKey"
                  label={copy.label}
                  required
                  code
                  value={draft.channelKey}
                  onChange={(channelKey) => patch({ channelKey })}
                  onBlur={() => check.touch('channelKey')}
                  error={check.errors.channelKey}
                  placeholder={copy.placeholder}
                  helperText={copy.helper}
                />
              )}
              <TextField
                id="provider-key-defaultFrom"
                testId="provider-key-defaultFrom"
                label="Default from"
                code
                value={draft.defaultFrom}
                onChange={(defaultFrom) => patch({ defaultFrom })}
                onBlur={() => check.touch('defaultFrom')}
                error={check.errors.defaultFrom}
                placeholder={draft.channel === 'sms' ? '+15555550123' : 'notifications@example.com'}
                helperText="The sender when a message names none."
              />
              <TextField
                id="provider-key-priority"
                testId="provider-key-priority"
                label="Priority"
                type="number"
                min={0}
                step={1}
                value={draft.priority}
                onChange={(priority) => patch({ priority })}
                onBlur={() => check.touch('priority')}
                error={check.errors.priority}
                helperText="Whole number, 0 or higher."
              />
              <ToggleField
                id="provider-key-isActive"
                testId="provider-key-isActive"
                label="Status"
                text="Active"
                checked={draft.isActive}
                onChange={(isActive) => patch({ isActive })}
              />
            </div>
          </CardSection>
        </CardBody>
        <CardFooter>
          <FormActions
            form={FORM_ID}
            discardHref={routes.notificationProviderKeys}
            {...(isNew
              ? { saveLabel: 'Create provider key', busyLabel: 'Creating…' }
              : {
                  disabled: !dirty,
                  disabledReason: 'No changes to save',
                  discardTestId: 'provider-key-discard',
                })}
            busy={busy}
            saveTestId="provider-key-save"
          />
        </CardFooter>
      </Card>
    </form>
  );
}
